import React, { useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import config from "config";

import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonContent,
  IonImg,
  IonText,
  IonToolbar,
  useIonAlert,
  IonIcon,
} from "@ionic/react";
import { alertCircleOutline } from "ionicons/icons";
import AppHeader from "components/Header/AppHeader";
import { useToasts } from "components/Toast/toast";
import Spacer from "components/spacer";

import { WSemit } from "services/socket";
import { nextPage, prevPage, loadQuiz } from "services/quiz";
import { getNextPage, getPreviousPage } from "services/quiz/utils";
import { logoutUser, syncUser } from "services/users";
import { setLoading } from "services/loading";
import { getQuizData, resetQuizData, resetStore } from "services/quiz";
import { clearTeams, loadTestTeamData } from "services/teams";
import ChromeCast from "services/chromecast";
import FinaliseButton from "components/FinaliseButton";
import Debug from "components/Debug";

const footerImage = "./assets/question-mark-779x600.png";

export interface Props {
  user: any;
  teams: any;
  quizData: Quiz;
  syncUser: Function;
  nextPage: Function;
  prevPage: Function;
  loadQuiz: Function;
  getQuizData: Function;
  setLoading: Function;
  resetStore: Function;
  resetQuizData: Function;
  clearTeams: Function;
  logoutUser: Function;
  loadTestTeamData: Function;
  ccConnected: boolean;
  playlist: any;
  playlistPosition: number;
  // socket: any;
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      ["google-cast-launcher"]: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

const Dashboard: React.FC<Props> = ({
  user,
  teams,
  quizData,
  loadQuiz,
  getQuizData,
  setLoading,
  resetStore,
  logoutUser,
  resetQuizData,
  loadTestTeamData,
  playlist,
  playlistPosition,
  // socket,
}) => {
  const history = useHistory();
  const [presentAlert] = useIonAlert();
  const { addToast } = useToasts();

  const { userDetails } = user;
  const progress: any = quizData.progress;
  const { quizId } = quizData;
  const { customerId } = user.userDetails;

  const [showPopover, setShowPopover] = useState(false);

  // useEffect(()=>{
  //   if(progress && progress.output && progress.output.mode === "quiz") history.push("/quiz");
  //   //eslint-disable-next-line
  // }, [progress.output])

  const onStartQuiz = () => {
    loadQuiz();
    let currentProgress = { ...progress };
    if (currentProgress.section === "intro") {
      currentProgress.section = "welcome";
      currentProgress.page = "";
    }

    WSemit("updateProgress", {
      location: "continueQuiz",
      ...currentProgress,
      output: { mode: "quiz", page: "" },
    });
    history.push("/quiz");
  };

  const downloadQuiz = () => {
    resetQuizData();
    clearTeams();
    setLoading(true, "Loading Quiz Data");
    getQuizData(user.userDetails.availableQuizId).then(() => {
      addToast({ message: "Quiz data ready", color: "success" });
      setLoading(false);
    });
  };

  const onDownloadQuiz = () => {
    if (teams.length > 0 || quizData.progress.round !== 0) {
      presentAlert({
        header: "Alert",
        subHeader: "You are about to download the quiz",
        message: "Downloading the new quiz will delete all your quiz data",
        buttons: [
          {
            text: "OK",
            role: "confirm",
            handler: () => {
              downloadQuiz();
            },
          },
          {
            text: "Cancel",
            role: "cancel",
          },
        ],
      });
    } else {
      downloadQuiz();
    }
  };

  const confirmResetStore = () => {
    presentAlert({
      header: "Alert",
      subHeader: "Reset Store",
      message:
        "This action will delete the local cache data and log you out. When you log in, the quiz progress should be reinstated from the database. Do you wish to continue?",
      buttons: [
        {
          text: "Cancel",
          role: "cancel",
        },
        {
          text: "Confirm",
          role: "confirm",
          handler: handleResetStore,
        },
      ],
    });
  };

  const handleResetStore = () => {
    resetStore();
    logoutUser();
  };

  const confirmResetProgress = () => {
    presentAlert({
      header: "Alert",
      subHeader: "Reset Progress",
      message:
        "This action will permanently delete the the progress and teams data for this quiz. This action can not be undone.  Do you wish to continue?",
      buttons: [
        {
          text: "Cancel",
          role: "cancel",
        },
        {
          text: "Confirm",
          role: "confirm",
          handler: handleResetProgress,
        },
      ],
    });
  };

  const handleResetProgress = () => {
    WSemit("clearData", { quizId, customerId });
  };

  const tooltipStyle: any = {
    visibility: showPopover ? "visible" : "hidden",
    backgroundColor: "gray",
    color: "#fff",
    width: "300px",
    textAlign: "center",
    padding: "5px 2px",
    borderRadius: "6px",
    fontSize: "12px",
    position: "absolute",
    zIndex: "2",
    top: "-12px",
  };

  const loadTeamData = (num: number) => () => {
    teams.forEach((team: any) => {
      WSemit("removeTeam", {
        teamId: team.teamId,
        quizId,
        customerId,
      });
    });

    let newTeamData = loadTestTeamData(num);

    newTeamData.forEach((teamData: any) => {
      console.log("teamData", teamData);
      WSemit("createTeam", {
        teamData: JSON.stringify(teamData),
        teamId: teamData.teamId,
        quizId,
        customerId,
      });
    });
  };

  const setNextSong = () => {
    const newPosition =
      playlistPosition >= playlist.length ? 1 : playlistPosition + 1;
    WSemit("nextSong", {
      playlistPosition: newPosition,
      progressId: progress.progressId,
      customerId,
      quizId,
    });
  };

  return (
    <>
      <AppHeader title="Dashboard" />

      <IonContent fullscreen className="ion-padding dashboard">
        <div className="container">
          <h2>Welcome, {userDetails.customerName}</h2>
          {/* <Spacer size="30" /> */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Quiz Status</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {userDetails.availableQuizId > 0 ? (
                <>
                  {userDetails.availableQuizId !== quizData.quizId &&
                    userDetails.availableQuizId > quizData.quizId && (
                      <IonCard>
                        <IonCardHeader>
                          <IonCardTitle>New Quiz Available</IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                          <p>
                            QuizId:
                            {userDetails.availableQuizId}
                          </p>
                          <IonBadge color="danger">
                            New Quiz data available
                          </IonBadge>

                          <IonToolbar>
                            <IonButton onClick={onDownloadQuiz}>
                              Download Now
                            </IonButton>
                          </IonToolbar>
                          <p>
                            <b>
                              You can not connect to the Chromecast until the
                              new quiz is downloaded.
                            </b>
                          </p>
                        </IonCardContent>
                      </IonCard>
                    )}
                  {quizData.quizId > 0 && (
                    <IonCard>
                      <IonCardHeader>
                        <IonCardTitle>Current Quiz</IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        <p>
                          Quiz: {quizData.quizName} (id: {quizData.quizId})
                        </p>

                        <IonBadge color="success">Quiz data loaded</IonBadge>
                      </IonCardContent>
                    </IonCard>
                  )}
                </>
              ) : (
                <>
                  <IonText color="danger">
                    <h3>We've encountered a problem!</h3>
                  </IonText>
                  <IonText color="danger">
                    Sorry, no quizzes are currently available for this account.
                    Please contact your Qwizard customer support representative.
                  </IonText>
                </>
              )}

              <IonCard style={{ overflow: "visible" }}>
                <IonCardContent style={{ overflow: "visible" }}>
                  {/* could be set in config */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "start",
                      alignItems: "center",
                      gap: "1px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>Output URL: </span>
                    <span>
                      <a
                        href={`${config.OUTPUT_URL}${user.userDetails.customerId}/${quizData.quizId}`}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {`${config.OUTPUT_URL}${user.userDetails.customerId}/${quizData.quizId}`}
                      </a>
                    </span>
                    <span
                      onMouseEnter={() => setShowPopover(true)}
                      onMouseLeave={() => setShowPopover(false)}
                      style={{
                        fontSize: "20px",
                        margin: "6px 0 0 2px",
                        position: "relative",
                      }}
                    >
                      <IonIcon icon={alertCircleOutline} />
                      <span style={tooltipStyle}>
                        This link provides you with an opportunity to present
                        your quiz on any browser.
                      </span>
                    </span>
                  </div>
                </IonCardContent>
              </IonCard>
              {quizData.quizId !== 0 && userDetails.availableQuizId > 0 && (
                <>
                  <Spacer size="15" />
                  <IonButton
                    size="large"
                    onClick={() => history.push("/teams")}
                  >
                    Register Teams
                  </IonButton>
                  {/* {ccConnected && ( */}
                  <IonButton
                    size="large"
                    onClick={() => {
                      onStartQuiz();
                    }}
                  >
                    {progress.section !== "intro"
                      ? "Continue Quiz"
                      : "Start Quiz"}
                  </IonButton>
                  {/* )} */}
                </>
              )}

              {/* {quizData.quizId !== 0 && ccConnected && ( */}
              <>
                <Spacer size="15" />
                <h2>Intro Slides</h2>

                <div>
                  <IonButton
                    size="small"
                    onClick={() => {
                      WSemit("updateProgress", {
                        location: "roundsButton",
                        ...progress,
                        output: { mode: "intro", page: 0, position: 0 },
                      });
                      ChromeCast.loadIntroRounds();
                    }}
                  >
                    Rounds
                  </IonButton>
                  {(user?.userDetails?.assignedPrizes?.length ||
                    (user?.userDetails?.prizeOptions === "custom" &&
                      user?.userDetails?.customPrizes?.length)) && (
                    <IonButton
                      size="small"
                      onClick={() => {
                        WSemit("updateProgress", {
                          location: "prizesButton",
                          ...progress,
                          output: { mode: "intro", page: 1, position: 0 },
                        });
                        ChromeCast.loadIntroPrizes();
                      }}
                    >
                      Prizes
                    </IonButton>
                  )}
                  <IonButton
                    size="small"
                    onClick={() => {
                      WSemit("updateProgress", {
                        location: "teamsButton",
                        ...progress,
                        output: { mode: "intro", page: 2, position: 0 },
                      });
                      ChromeCast.loadIntroTeams();
                    }}
                  >
                    Teams
                  </IonButton>
                  {/* <IonButton>Crossword</IonButton> */}
                </div>
              </>
              {/* )} */}

              {quizData?.progress?.round >=
                quizData?.quizConfig?.format?.rounds &&
                (quizData.progress.page === "winner" ||
                  (quizData.progress.page === "result" &&
                    [2, 3].includes(quizData.progress.position))) &&
                (!quizData.resultsSubmitted || quizData.resultsAmended) && (
                  <>
                    <Spacer size="30" />
                    <FinaliseButton trigger={false} />
                  </>
                )}
            </IonCardContent>
          </IonCard>

          <Spacer size="15" />
          {config.TEST_MODE && quizData && quizData.quizId !== 0 && (
            <>
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>App Tools</IonCardTitle>
                  <IonCardSubtitle>
                    These actions are automatically hidden on the production app
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonButton onClick={confirmResetStore}>Reset Store</IonButton>
                  <IonButton onClick={confirmResetProgress}>
                    Reset Progress
                  </IonButton>
                  <IonButton onClick={onDownloadQuiz}>
                    {quizData.quizId === 0
                      ? "Download Quiz"
                      : "Redownload Quiz"}
                  </IonButton>
                </IonCardContent>
              </IonCard>
              <Spacer size="15" />
            </>
          )}

          {config.TEST_MODE && (
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Chromecast Test Actions</IonCardTitle>
                <IonCardSubtitle>
                  These actions are automatically hidden on the production app
                </IonCardSubtitle>
                <IonCardSubtitle>
                  Connecting to ChromeCast receiver {config.CC_APP_ID} using API{" "}
                  {config.API_URL}
                </IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <h2>Intro</h2>

                {/* <IonButton onClick={ChromeCast.loadIntro}>Intro</IonButton> */}
                <IonButton
                  onClick={() => {
                    WSemit("updateProgress", {
                      location: "roundsTestButton",
                      ...progress,
                      output: { mode: "intro", page: 0 },
                    });
                    // ChromeCast.loadIntroRounds();
                  }}
                >
                  Rounds
                </IonButton>
                <IonButton
                  onClick={() => {
                    WSemit("updateProgress", {
                      location: "prizesTestButton",
                      ...progress,
                      output: { mode: "intro", page: 0 },
                    });
                    // ChromeCast.loadIntroPrizes();
                  }}
                >
                  Prizes
                </IonButton>
                <IonButton
                  onClick={() => {
                    WSemit("updateProgress", {
                      location: "teamsTestButton",
                      ...progress,
                      output: { mode: "intro", page: 2 },
                    });
                    // ChromeCast.loadIntroTeams();
                  }}
                >
                  Teams
                </IonButton>
                {/* <IonButton onClick={ChromeCast.showAds}>Show Ads</IonButton>
                <IonButton onClick={ChromeCast.hideAds}>Hide Ads</IonButton> */}
                <Spacer size="15" />

                <h2>Teams</h2>

                <IonButton onClick={loadTeamData(12)}>
                  Update Teams 12
                </IonButton>
                <IonButton onClick={loadTeamData(24)}>
                  Update Teams 24
                </IonButton>
                <IonButton onClick={loadTeamData(26)}>
                  Update Teams 26
                </IonButton>

                <Spacer size="15" />
                <h2>Quiz Progress</h2>
                {/* Loads a test config into Chromecast app */}
                {/* <IonButton onClick={ChromeCast.loadTestConfig}>
                  Load Test Config
                </IonButton> */}
                {/* Loads the current quiz config into Chromecast app */}
                {/* <IonButton
                  onClick={() =>
                    ChromeCast.loadQuizConfig({ user, quizData, teams })
                  }
                >
                  Load Quiz Config
                </IonButton> */}
                {/* Tells the app to display the quiz should the view be on Intro. This is called on clicking */}
                <IonButton onClick={() => loadQuiz()}>Load Quiz</IonButton>
                {/* Sets progress to the Welcome screen */}
                <IonButton
                  onClick={() =>
                    WSemit("updateProgress", {
                      location: "loadWelcomeButton",
                      ...progress,
                      section: "welcome",
                      output: { mode: "quiz", page: "" },
                    })
                  }
                >
                  Load Welcome
                </IonButton>
                <IonButton
                  onClick={() => {
                    const newValues = getPreviousPage(
                      quizData,
                      user.userDetails,
                      teams,
                      progress
                    );
                    WSemit("updateProgress", {
                      location: "prevButton",
                      ...progress,
                      ...newValues,
                    });
                  }}
                >
                  Prev
                </IonButton>
                <IonButton
                  onClick={() => {
                    const newValues = getNextPage(
                      quizData,
                      user.userDetails,
                      teams,
                      progress
                    );
                    WSemit("updateProgress", {
                      location: "nextButton",
                      ...progress,
                      ...newValues,
                    });
                  }}
                >
                  Next
                </IonButton>
                <IonButton
                  onClick={() => {
                    // ChromeCast.jumpToPuzzle();
                    WSemit("updateProgress", {
                      location: "puzzleButton",
                      ...progress,
                      section: "crossword",
                      page: "question",
                      output: { mode: "quiz", page: "" },
                    });
                  }}
                >
                  Puzzle
                </IonButton>
                <IonButton
                  onClick={() => {
                    // ChromeCast.jumpToBreaker();
                    WSemit("updateProgress", {
                      location: "breakerButton",
                      ...progress,
                      section: "breaker",
                      page: "question",
                      output: { mode: "quiz", page: "" },
                    });
                  }}
                >
                  Breaker
                </IonButton>
                <Spacer size="15" />

                <h2>Output App</h2>
                <IonButton onClick={ChromeCast.toggleDebug}>
                  Toggle Debug
                </IonButton>
                <IonButton
                  onClick={() => {
                    // ChromeCast.playMusic();
                    WSemit("updateMusicActive", {
                      musicActive: 1,
                      progressId: progress.progressId,
                      quizId,
                      customerId,
                    });
                    setNextSong();
                  }}
                >
                  Play Music
                </IonButton>
                <IonButton
                  onClick={() => {
                    // ChromeCast.pauseMusic();
                    WSemit("updateMusicActive", {
                      musicActive: 0,
                      progressId: progress.progressId,
                      quizId,
                      customerId,
                    });
                    setNextSong();
                  }}
                >
                  Pause Music
                </IonButton>
                <Spacer size="15" />
                <h2>ChromeCast</h2>
                <IonButton onClick={ChromeCast.refresh}>Refresh</IonButton>
                <IonButton onClick={ChromeCast.reload}>Reload</IonButton>
                <IonButton onClick={ChromeCast.quit}>Quit</IonButton>
              </IonCardContent>
            </IonCard>
          )}
          <Spacer size="15" />
          <Debug />
          <Spacer size="30" />
          <IonImg
            src={footerImage}
            alt="6 subjects"
            style={{ maxWidth: "600px", margin: "0 auto" }}
          />
          <Spacer size="90" />
        </div>
      </IonContent>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.user,
  quizData: state.quiz,
  teams: state.teams.teams,
  ccConnected: state.general.ccConnected,
  playlist: state.user.userDetails.songPlaylist,
  playlistPosition: state.quiz.progress.playlistPosition,
});

export default connect(mapStateToProps, {
  syncUser,
  logoutUser,
  nextPage,
  prevPage,
  loadQuiz,
  getQuizData,
  setLoading,
  resetStore,
  resetQuizData,
  clearTeams,
  loadTestTeamData,
})(Dashboard);
